import * as THREE from 'three';
import { Rng } from '../../core/rng';
import type { Rect } from '../../core/math';
import type { Decor } from '../decor';
import type { CityLayout } from '../types';
import { WATER_Y } from './ground';
import { MeshBuilder } from './meshBuilder';

/**
 * Low mist lying in the cut of the canal and over the basin: three thin layers of drifting soft
 * noise just above the water, below quay level. One mesh, one draw call; colour and density follow
 * the mood (warm grey by day, slate at dusk, blue-black at night). Bridge decks are left clear.
 */
export interface RiverHaze {
  readonly mesh: THREE.Mesh;
  setMood(color: number, opacity: number): void;
  update(time: number): void;
  dispose(): void;
}

function mistTexture(): THREE.CanvasTexture {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  g.fillStyle = '#2a2a2a';
  g.fillRect(0, 0, size, size);
  const rng = new Rng(1971);
  for (let i = 0; i < 46; i++) {
    const x = rng.next() * size;
    const y = rng.next() * size;
    const r = rng.range(36, 96);
    const a = rng.range(0.05, 0.13);
    for (const [ox, oy] of [
      [0, 0],
      [size, 0],
      [-size, 0],
      [0, size],
      [0, -size],
      [size, size],
      [-size, -size],
      [size, -size],
      [-size, size],
    ] as const) {
      const grad = g.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, r);
      grad.addColorStop(0, `rgba(255,255,255,${a})`);
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grad;
      g.beginPath();
      g.ellipse(x + ox, y + oy, r * 1.8, r, 0, 0, Math.PI * 2);
      g.fill();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

export function createRiverHaze(decor: Decor, layout: CityLayout): RiverHaze {
  const b = new MeshBuilder();
  const EXT = 700;
  const keep: Rect[] = decor.bridges.map((br) => {
    const e = layout.roads.edges[br.edgeId]!;
    const x = layout.roads.nodes[e.from]!.pos.x;
    const hw = e.width / 2 + e.sidewalk + 2;
    return { minX: x - hw, minZ: br.z0, maxX: x + hw, maxZ: br.z1 };
  });
  const TILE = 10;
  const layers = [WATER_Y + 0.35, WATER_Y + 0.85, WATER_Y + 1.3];
  for (const w of decor.water) {
    const x0 = Math.max(w.minX, -EXT);
    const x1 = Math.min(w.maxX, EXT);
    const z0 = Math.max(w.minZ, -EXT) + 0.3;
    const z1 = Math.min(w.maxZ, EXT);
    for (let x = x0; x < x1; x += TILE) {
      const xb = Math.min(x + TILE, x1);
      if (keep.some((r) => xb > r.minX && x < r.maxX && z1 > r.minZ && z0 < r.maxZ)) continue;
      for (let z = z0; z < z1; z += TILE * 4) {
        const zb = Math.min(z + TILE * 4, z1);
        layers.forEach((y, li) => {
          // world-space UVs (per layer offset/scale so the layers drift apart)
          const s = 1 / (34 + li * 13);
          const o = li * 0.37;
          b.quad([x, y, zb], [xb, y, zb], [xb, y, z], [x, y, z], [x * s + o, -zb * s + o, xb * s + o, -z * s + o], [1, 1, 1]);
        });
      }
    }
  }
  const geo = b.build() ?? new THREE.BufferGeometry();
  const tex = mistTexture();
  const mat = new THREE.MeshBasicMaterial({ color: 0x8a8d94, alphaMap: tex, transparent: true, opacity: 0.25, depthWrite: false, fog: true });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'river-haze';
  mesh.renderOrder = 3;
  mesh.matrixAutoUpdate = false;
  mesh.frustumCulled = false;
  return {
    mesh,
    setMood(color, opacity) {
      mat.color.setHex(color);
      mat.opacity = opacity;
    },
    update(time) {
      tex.offset.set(time * 0.0035, time * 0.0012);
    },
    dispose() {
      geo.dispose();
      mat.dispose();
      tex.dispose();
    },
  };
}
